import {useEffect, useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

import {SwalAlertChoice, SwalAlertClass} from "core/swalClass";
import NavBreadcrum from "components/NavBreadcrum";
import TopBarActions from "components/TopBarActions";
import axiApi from "ApiCaller";
import ButtonsActions from "views/utils/buttonsActions";
import navs from "./navs";
import TableAlerts from "./alerts/TableAlerts";
import LoaderSpinner from "context/LoaderSpinner";
import FilterModel from "components/FilterModel";
import TopBarPagination from "components/paginator/TopBarPagination-v2";
import {PagerModel} from "components/paginator/PaginationModel";
import ItemsModel from "core/itemsModel";
import {AlertDeviceConfigModel} from "site-apps/alerts/AlertSetupModels";
import translations from "./alerts/translations";
import useCustomTranslation from "core/customTranslation";
import OrderingModel from "components/ordering/OrderingModel";

const ViewProfilesAlerts = () => {
    const {t: _Tr} = useTranslation("routes");
    const {t: _Tf} = useTranslation("forms");
    const {t: _Tc} = useCustomTranslation(translations);
    const navigate = useNavigate();

    const bread = [
        {
            name: _Tr("labels.device-configuration"),
            url: "/device-configuration/",
            current: false,
        },
        {name: _Tr("configuration.alerts"), current: true},
    ];
    document.title = _Tr("configuration.alerts");
    const [buttonsTB, setButtonsTB] = useState(() => {
        const buttonsAct = new ButtonsActions();
        buttonsAct.title = document.title;
        return buttonsAct;
    });
    const AlertQuestion = new SwalAlertChoice({
        icon: "info",
        iconColor: "#DD6B55",
        confirmButtonColor: "#DD6B55",
        confirmButtonText: _Tf("labels.delete"),
        cancelButtonText: _Tf("labels.cancel"),
    });
    const Alert = new SwalAlertClass({confirmButtonText: _Tf("labels.accept")});
    const items = useRef(new ItemsModel());
    const filters = useRef(new FilterModel());
    const pagerModelRef = useRef(new PagerModel());
    const orderingRef = useRef(new OrderingModel());

    const [itemsShow, setItemsShow] = useState([]);
    const [item, setItem] = useState({...AlertDeviceConfigModel});
    const [checkedAll, setCheckedAll] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [ShowPrev, setShowPrev] = useState(false);
    const headersTable = [
        {name: _Tf("common.name"), attr: 0, direction: "asc", active: true, default: true},
        {name: _Tc("description"), attr: 1, direction: "asc", active: false},
        {name: _Tc("params"), attr: 2, direction: "asc", active: false},
    ];

    useEffect(() => {
        const buttons = {...buttonsTB};
        buttons.title = document.title;
        buttons.add.active = true;
        buttons.add.onClick = () => {
            navigate("/device-configuration/alert/setup/add/");
        };
        buttons.add.disabled = false;
        buttons.edit.active = true;
        buttons.edit.disabled = true;
        buttons.edit.onClick = onClickEdit;
        buttons.delete.active = true;
        buttons.delete.disabled = true;
        buttons.delete.onClick = onClickDelete;
        setButtonsTB(buttons);
        pagerModelRef.current.active = true;
        pagerModelRef.current.handleCallApi = () => {
            fetchAlerts();
        };
        orderingRef.current.setHeaders(headersTable);
        orderingRef.current.applyOrder = () => {
            fetchAlerts();
        };
        items.current.setItem = setItem;
        items.current.item = AlertDeviceConfigModel;
        items.current.setShowPrev = setShowPrev;

        fetchGetAlerts();
    }, []);
    filters.current.filter = () => fetchAlerts();

    const fetchGetAlerts = () => {
        setIsLoading(true);
        axiApi
            .get("/api/devices/configs/alerts/")
            .then((resp) => {
                if (resp.data.status) {
                    items.current.showed = [...resp.data.items];
                    setItemsShow([...items.current.showed]);
                    if (resp.data.pager) {
                        pagerModelRef.current.paging.update(resp.data.pager);
                    }
                } else {
                    console.error("Error en alertSetup.api()");
                    Alert.warning("Error en el servicio", "No se han podido recuperar las alertas");
                }
            })
            .finally(() => setIsLoading(false));
    };

    const fetchAlerts = () => {
        setIsLoading(true);
        axiApi
            .post("/api/devices/configs/alerts/", filters.current.chosen, {
                params: {...pagerModelRef.current.paging.getParams(), ...orderingRef.current.getParams()},
            })
            .then((resp) => {
                if (resp.data.status) {
                    items.current.setShowed(resp.data.items);
                    setItemsShow([...resp.data.items]);
                    if (resp.data.pager) {
                        pagerModelRef.current.paging.update(resp.data.pager);
                    }
                } else {
                    console.error("Error en alertSetup.api()");
                    Alert.warning("Error en el servicio", "No se han podido recuperar las alertas");
                }
            })
            .finally(() => setIsLoading(false));
    };

    const onClickEdit = (e) => {
        if (!items.current.chosen.length || items.current.chosen.length > 1) {
            return false;
        }
        let chosen = items.current.chosen[0];
        navigate(`/device-configuration/alert/setup/${chosen}/`);
    };

    const onClickDelete = (e) => {
        AlertQuestion.fire("Se eliminaran", "Todos los seleccionados").then((r) => {
            if (r.isConfirmed) {
                let ids = items.current.getChosenIds();
                setIsLoading(true);
                axiApi
                    .delete("/api/devices/configs/alerts/delete/", {
                        data: {ids: ids},
                    })
                    .then((r) => {
                        if (r.data.status) {
                            Alert.success("Confirmado", "Se han eliminado correctamente");
                            // Filtra los items eliminados
                            items.current.showed = items.current.showed.filter((item) => !ids.includes(item.id));
                            items.current.clearChosen();
                            setItemsShow([...items.current.showed]);
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                        if (err.response.status === 404) {
                            Alert.error("Error en el servidor", "El id enviado no esta accesible.");
                        } else {
                            Alert.error("Error en el servidor", "Consultar con el administrador.");
                        }
                    })
                    .finally(() => {
                        const buttons = {...buttonsTB};
                        buttons.add.disabled = true;
                        buttons.edit.disabled = true;
                        buttons.delete.disabled = true;
                        setButtonsTB(buttons);
                        setIsLoading(false);
                    });
            }
        });
    };

    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <TopBarActions actions={buttonsTB} filtersRef={filters} />
            <TopBarPagination pagerModel={pagerModelRef.current} stickyTopValue="125px" chosen={items.current.chosen} />
            <LoaderSpinner isLoading={isLoading}>
                <TableAlerts
                    items={items}
                    itemsShow={itemsShow}
                    buttonsTB={buttonsTB}
                    setButtonsTB={setButtonsTB}
                    checkAll={checkedAll}
                    setCheckAll={setCheckedAll}
                    orderingRef={orderingRef}
                />
            </LoaderSpinner>
        </>
    );
};
export default ViewProfilesAlerts;
